/**
 * Связь окна карточки площадки с карточкой ПИМ.
 *
 * Окно знает строку зеркала площадки, а правка идёт по `marketplace_cards.id`.
 * Этот id даёт присутствие артикула (`pim_card.id` по площадке и кабинету).
 * Нет `pim_card` — управление карточкой этого кабинета не зарегистрировано:
 * это не означает отсутствия связи с товаром/макетом. Окно остаётся на чтении.
 * Состояние окна (значения ПИМ с правками, рубильники,
 * история) читается с бэкенда по найденному id.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useProductPresence, type PresenceData } from '../../../products-v2/presence'
import type { ProductPresenceEntry } from '../../../products-v2/types'
import type { LinkState } from './checks'
import type { McardPlatform } from './model'
import { cardWindowApi, windowEditErrorInfo, type CardWindowState } from './windowEditApi'

export interface MpCardLink {
  presence: PresenceData
  entry: ProductPresenceEntry | null
  /** `marketplace_cards.id` управления карточкой кабинета; null — не зарегистрировано. */
  cardId: number | null
  link: LinkState
  state: CardWindowState | null
  stateLoading: boolean
  /** Почему состояния окна нет (для чипа «нет данных»). */
  stateWhy: string | null
  reloadState: () => void
  /** Перечитать и присутствие, и состояние (после правки). */
  reloadAll: () => void
}

interface Options {
  marketplace: McardPlatform
  sellerId: number | null
  article: string | null | undefined
}

export function useMpCardLink({ marketplace, sellerId, article }: Options): MpCardLink {
  const presence = useProductPresence(article || null)
  const entry = useMemo(() => {
    if (!presence.entries) return null
    return (
      presence.entries.find(
        (candidate) =>
          candidate.marketplace === marketplace &&
          (sellerId === null || Number(candidate.seller_id) === Number(sellerId)),
      ) ?? null
    )
  }, [presence.entries, marketplace, sellerId])

  const cardId = entry?.pim_card?.id ?? null
  const windowEditable = marketplace !== 'site' && sellerId !== null

  const [state, setState] = useState<CardWindowState | null>(null)
  const [stateLoading, setStateLoading] = useState(false)
  const [stateWhy, setStateWhy] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const reloadState = useCallback(() => setTick((value) => value + 1), [])
  const reloadAll = useCallback(() => {
    presence.reload()
    setTick((value) => value + 1)
  }, [presence])

  useEffect(() => {
    if (!windowEditable || cardId === null || sellerId === null) {
      setState(null)
      setStateLoading(false)
      setStateWhy(
        !windowEditable
          ? 'у этой площадки нет сравнения с ПИМ'
          : presence.entries === null
            ? 'присутствие на площадках ещё загружается'
            : 'управление карточкой этого кабинета не зарегистрировано в ПИМ',
      )
      return undefined
    }
    const controller = new AbortController()
    setStateLoading(true)
    cardWindowApi
      .getState(sellerId, cardId, controller.signal)
      .then((value) => {
        if (controller.signal.aborted) return
        setState(value)
        setStateWhy(null)
      })
      .catch((error: any) => {
        if (controller.signal.aborted || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return
        const info = windowEditErrorInfo(error, 'Не удалось прочитать состояние карточки в ПИМ')
        setState(null)
        setStateWhy(info.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setStateLoading(false)
      })
    return () => controller.abort()
    // При завершении presence без записи cardId остаётся null, но причина уже известна.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, sellerId, windowEditable, tick, presence.entries === null])

  let link: LinkState
  if (presence.entries === null) link = presence.error ? 'error' : 'loading'
  else if (cardId === null) link = 'not_linked'
  else link = 'linked'

  return { presence, entry, cardId, link, state, stateLoading, stateWhy, reloadState, reloadAll }
}
